import type {
  ApplicantSession,
  LoginResult,
  MeResponse,
  ResellerPermission,
  SessionUser,
} from "@xop-any-time/shared";
import { isTwoFactorChallenge } from "@xop-any-time/shared";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from "react";

import { ApiError, api } from "../lib/api.js";

export interface LoginInput {
  email: string;
  password: string;
  /** Present only on the second leg of a two-factor sign-in. */
  otp?: string;
}

export type LoginOutcome =
  | { kind: "ok"; user: SessionUser }
  /** portal-api wants a second factor. Not an error — the credentials
   * were correct; the flow simply has another step. */
  | { kind: "two_factor"; method: "totp" | "email"; emailHint: string | null }
  /** Signed in as an APPLICANT (spec E §6.2): no membership yet, an open
   * application. The only page for them is the onboarding wall. */
  | { kind: "applicant"; applicant: ApplicantSession }
  | { kind: "error"; message: string; code?: string };

interface AuthApi {
  user: SessionUser | null;
  /**
   * Set INSTEAD of `user` for someone whose application is still open. At
   * most one of the two is non-null — the BFF's cookie is one token type or
   * the other. `RequireAuth` routes an applicant to `/onboarding`;
   * `RequireApplicant` routes everyone else away from it.
   */
  applicant: ApplicantSession | null;
  /**
   * `loading` until the boot-time `/api/auth/me` settles.
   *
   * This matters more than it looks: without it, a page refresh on a
   * signed-in session renders `user === null` for one frame, `RequireAuth`
   * redirects to /login, and the user is bounced out of the app every time
   * they hit reload. The status is what lets the router wait.
   */
  status: "loading" | "ready";
  /** The signed-in person's effective permission set (spec F §6.3). Empty
   * when signed out or before boot settles. RENDERING ONLY — see `can`. */
  permissions: ResellerPermission[];
  /**
   * Whether `permissions` holds a key. NOT a gate: every write this app
   * makes is refused or allowed by crm-api from the live membership, never
   * from this array. Hiding a control here is a courtesy; showing one can
   * still be refused server-side.
   */
  can: (key: ResellerPermission) => boolean;
  login: (input: LoginInput) => Promise<LoginOutcome>;
  /**
   * Re-read `/api/auth/me` and adopt whatever session the cookie now names.
   *
   * For flows where the BFF establishes the session on a route other than
   * `/login` — accepting an invitation sets the password and signs in as one
   * request — so the SPA has a signed-in cookie but a context that still says
   * null. Nothing here mints a session; it only asks who the cookie is.
   */
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Forget the session locally, WITHOUT telling the BFF.
   *
   * The difference from `logout` is who decided. `logout` is the reseller
   * ending a live session, so it must reach the server to clear the cookie.
   * This is the server having already told US the session is gone — a 401
   * from the CRM passthrough — so a POST to `/api/auth/logout` would be a
   * round trip to end something that no longer exists.
   *
   * Called only by `SessionExpiryBridge`. Without it a soft navigation to
   * /login would arrive with `user` still set, and `LoginPage` would send the
   * reseller straight back to the dead page, which 401s again: a loop.
   */
  expireSession: () => void;
}

const ctx = createContext<AuthApi | null>(null);

/**
 * Session context.
 *
 * The SPA holds NO token. The session lives in an httpOnly cookie the BFF
 * sets (`apps/server/src/auth/session.ts`), which is why every call here
 * goes through `api()` with `credentials: "include"` and why signing out
 * is a server round trip rather than clearing local state. Nothing in this
 * file can read or forge a session — it only ever asks the BFF who it is.
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [applicant, setApplicant] = useState<ApplicantSession | null>(null);
  const [status, setStatus] = useState<"loading" | "ready">("loading");
  const [permissions, setPermissions] = useState<ResellerPermission[]>([]);

  // Hydrate once on mount. `/api/auth/me` answers 200 with a null user
  // when signed out, so a failure here is a real outage, not "logged out".
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await api<MeResponse>("/api/auth/me");
        if (!cancelled) {
          setUser(data.user);
          setApplicant(data.applicant ?? null);
          setPermissions(data.permissions ?? []);
        }
      } catch {
        // Treat an unreachable BFF as signed-out rather than hanging on a
        // spinner forever — the login page can at least show an error.
        // Both halves of the session, for symmetry with the success branch
        // above (and with `refresh`'s own `setUser` + `setApplicant` pair).
        if (!cancelled) {
          setUser(null);
          setApplicant(null);
          setPermissions([]);
        }
      } finally {
        if (!cancelled) setStatus("ready");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (input: LoginInput): Promise<LoginOutcome> => {
    try {
      const data = await api<LoginResult>("/api/auth/login", {
        method: "POST",
        body: input,
      });

      // A 2FA challenge arrives as a 200 with a different body shape —
      // see the note on `TwoFactorChallenge` in @xop-any-time/shared.
      if (isTwoFactorChallenge(data)) {
        return { kind: "two_factor", method: data.method, emailHint: data.emailHint };
      }

      if (data.applicant) {
        setUser(null);
        setApplicant(data.applicant);
        setPermissions([]);
        return { kind: "applicant", applicant: data.applicant };
      }
      if (!data.user) {
        return { kind: "error", message: "Something went wrong. Please try again." };
      }
      setApplicant(null);
      setPermissions(data.permissions ?? []);
      setUser(data.user);
      return { kind: "ok", user: data.user };
    } catch (err) {
      if (err instanceof ApiError) {
        return err.code
          ? { kind: "error", message: err.message, code: err.code }
          : { kind: "error", message: err.message };
      }
      return { kind: "error", message: "Something went wrong. Please try again." };
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await api<MeResponse>("/api/auth/me");
      setUser(data.user);
      setApplicant(data.applicant ?? null);
      setPermissions(data.permissions ?? []);
    } catch {
      // Same reading as the boot-time hydrate: an unreachable BFF is not a
      // sign-out. Leave whatever the context already holds.
    }
  }, []);

  const logout = useCallback(async () => {
    // Clear locally FIRST so the UI never shows a signed-in shell while a
    // slow round trip completes; the cookie is cleared by the server call.
    setUser(null);
    setApplicant(null);
    setPermissions([]);
    try {
      await api("/api/auth/logout", { method: "POST" });
    } catch {
      // Best-effort. The cookie may survive, but the session it names will
      // expire on its own and nothing in the UI trusts local state for
      // authorization — every data call is checked server-side.
    }
  }, []);

  const expireSession = useCallback(() => {
    // Exactly what `logout` clears, minus the round trip — see the note on
    // `expireSession` in `AuthApi` for why there is nothing to tell the BFF.
    setUser(null);
    setApplicant(null);
    setPermissions([]);
  }, []);

  const can = useCallback((key: ResellerPermission) => permissions.includes(key), [permissions]);

  const value = useMemo<AuthApi>(
    () => ({ user, applicant, status, permissions, can, login, refresh, logout, expireSession }),
    [user, applicant, status, permissions, can, login, refresh, logout, expireSession],
  );

  return <ctx.Provider value={value}>{children}</ctx.Provider>;
}

export function useAuth(): AuthApi {
  const value = useContext(ctx);
  if (!value) throw new Error("useAuth must be used within an AuthProvider");
  return value;
}
